import { supabase } from '@/lib/integrations/supabase'
import { getUserUnitsByContext } from '@/modules/units'
import type { StoredDocument } from '@/modules/documents'
import type {
  AccountingClient,
  AccountingClientInput,
  AccountingClientReference,
  AccountingClientResetResult,
  LinkAccountingClientInput,
  LinkableClientUnit,
} from '@/modules/accounting/clients/accountingClientTypes'

const DOCUMENTS_BUCKET = 'documents'
const RESET_STORAGE_CHUNK_SIZE = 100

async function listClientDocumentStoragePaths(clientUnitId: string): Promise<string[]> {
  const pageSize = 1000
  const paths = new Set<string>()

  for (const table of ['documents', 'document_batches'] as const) {
    let page = 0
    while (true) {
      const column = table === 'documents' ? 'storage_path' : 'original_storage_path'
      const { data, error } = await supabase
        .from(table)
        .select(column)
        .eq('unit_id', clientUnitId)
        .range(page * pageSize, ((page + 1) * pageSize) - 1)

      if (error) throw error
      const rows = (data || []) as unknown as Array<Record<string, string | null>>
      rows.forEach((row) => {
        const path = row[column]
        if (path) paths.add(path)
      })
      if (rows.length < pageSize) break
      page += 1
    }
  }

  return Array.from(paths)
}

async function removeClientDocumentStorage(paths: string[]): Promise<void> {
  for (let index = 0; index < paths.length; index += RESET_STORAGE_CHUNK_SIZE) {
    const { error } = await supabase.storage
      .from(DOCUMENTS_BUCKET)
      .remove(paths.slice(index, index + RESET_STORAGE_CHUNK_SIZE))
    if (error) throw error
  }
}

export type AccountingClientStatsDocument = Pick<
  StoredDocument,
  'id' | 'unit_id' | 'document_kind' | 'status' | 'original_name' | 'storage_path' | 'metadata'
>

export async function listAccountingClientStatsDocuments(
  clientUnitIds: string[],
): Promise<AccountingClientStatsDocument[]> {
  if (clientUnitIds.length === 0) return []

  const pageSize = 1000
  const documents: AccountingClientStatsDocument[] = []
  let page = 0

  while (true) {
    const { data, error } = await supabase
      .from('documents')
      .select('id, unit_id, document_kind, status, original_name, storage_path, metadata')
      .in('unit_id', clientUnitIds)
      .neq('status', 'rejected')
      .range(page * pageSize, ((page + 1) * pageSize) - 1)

    if (error) throw error

    const currentPage = (data || []) as AccountingClientStatsDocument[]
    documents.push(...currentPage)
    if (currentPage.length < pageSize) return documents
    page += 1
  }
}

export async function listAccountingClients(officeUnitId: string): Promise<AccountingClient[]> {
  const { data, error } = await supabase.rpc('list_accounting_clients', {
    p_office_unit_id: officeUnitId,
  })

  if (error) throw error

  const clients = (data || []) as Omit<AccountingClient, 'tax_regime' | 'activity_kind'>[]
  if (clients.length === 0) return []

  const { data: clientUnits, error: unitsError } = await supabase.rpc(
    'list_accounting_client_fiscal_profiles',
    { p_office_unit_id: officeUnitId },
  )

  if (unitsError) throw unitsError

  const profileByUnit = new Map<string, { tax_regime: string | null; activity_kind: AccountingClient['activity_kind'] }>(
    (clientUnits || []).map((unit: { client_unit_id: string; tax_regime: string | null; activity_kind: AccountingClient['activity_kind'] }) => [unit.client_unit_id, unit]),
  )

  return clients.map((client) => ({
    ...client,
    tax_regime: profileByUnit.get(client.client_unit_id)?.tax_regime || null,
    activity_kind: profileByUnit.get(client.client_unit_id)?.activity_kind || null,
  }))
}

export async function createAccountingClient(
  officeUnitId: string,
  input: AccountingClientInput,
): Promise<AccountingClientReference> {
  const { data, error } = await supabase.rpc('create_accounting_client', {
    p_office_unit_id: officeUnitId,
    p_name: input.name.trim(),
    p_legal_name: input.legal_name?.trim() || null,
    p_trade_name: input.trade_name?.trim() || null,
    p_cnpj: input.cnpj?.trim() || null,
    p_whatsapp: input.whatsapp?.trim() || null,
    p_notes: input.notes?.trim() || null,
  })

  if (error) throw error

  const created = Array.isArray(data) ? data[0] : data
  if (!created) throw new Error('accounting_client_create_failed')

  const reference = created as AccountingClientReference
  if (input.tax_regime || input.activity_kind) {
    await updateAccountingClient(officeUnitId, reference.party_id, input)
  }

  return reference
}

export async function linkAccountingClientUnit(
  officeUnitId: string,
  input: LinkAccountingClientInput,
): Promise<AccountingClientReference> {
  const { data, error } = await supabase.rpc('link_accounting_client_unit', {
    p_office_unit_id: officeUnitId,
    p_client_unit_id: input.client_unit_id,
    p_name: input.name.trim(),
    p_whatsapp: input.whatsapp?.trim() || null,
    p_notes: input.notes?.trim() || null,
  })

  if (error) throw error

  const linked = Array.isArray(data) ? data[0] : data
  if (!linked) throw new Error('accounting_client_link_failed')

  return linked as AccountingClientReference
}

export async function updateAccountingClient(
  officeUnitId: string,
  partyId: string,
  input: AccountingClientInput,
): Promise<void> {
  const { error } = await supabase.rpc('update_accounting_client', {
    p_office_unit_id: officeUnitId,
    p_party_id: partyId,
    p_name: input.name.trim(),
    p_legal_name: input.legal_name?.trim() || null,
    p_trade_name: input.trade_name?.trim() || null,
    p_cnpj: input.cnpj?.trim() || null,
    p_tax_regime: input.tax_regime?.trim() || null,
    p_activity_kind: input.activity_kind?.trim() || null,
    p_whatsapp: input.whatsapp?.trim() || null,
    p_notes: input.notes?.trim() || null,
  })

  if (error) throw error
}

export async function resetAccountingClient(
  officeUnitId: string,
  clientUnitId: string,
): Promise<AccountingClientResetResult> {
  const storagePaths = await listClientDocumentStoragePaths(clientUnitId)
  await removeClientDocumentStorage(storagePaths)

  const { data, error } = await supabase.rpc('reset_accounting_client_workspace', {
    p_office_unit_id: officeUnitId,
    p_client_unit_id: clientUnitId,
  })

  if (error) throw error
  return data as AccountingClientResetResult
}

export async function listLinkableClientUnits(officeUnitId: string): Promise<LinkableClientUnit[]> {
  const { data: linkedParties, error: partiesError } = await supabase
    .from('parties')
    .select('linked_unit_id')
    .eq('unit_id', officeUnitId)
    .not('linked_unit_id', 'is', null)

  if (partiesError) throw partiesError

  const linkedIds = new Set(
    (linkedParties || [])
      .map((party: { linked_unit_id: string | null }) => party.linked_unit_id)
      .filter((id): id is string => Boolean(id)),
  )

  const { data: authData, error: authError } = await supabase.auth.getUser()
  if (authError) throw authError
  if (!authData.user) return []

  const units = await getUserUnitsByContext(authData.user.id, 'accounting')

  const unique = new Map<string, LinkableClientUnit>()

  for (const candidate of units) {
    const unit = candidate as any
    if (!unit || unit.id === officeUnitId || linkedIds.has(unit.id)) continue

    unique.set(unit.id, {
      id: unit.id,
      name: unit.name,
      legal_name: unit.legal_name || null,
      trade_name: unit.trade_name || null,
      cnpj: unit.cnpj || null,
    })
  }

  return Array.from(unique.values()).sort((left, right) => left.name.localeCompare(right.name))
}
